// Default color config (age-based) - overridden by backend config
const DEFAULT_YEAR_COLORS = [
  { age: 0, name: 'verde', hex: '#22c55e' },
  { age: 1, name: 'azul', hex: '#2563eb' },
  { age: 2, name: 'rojo', hex: '#ef4444' },
  { age: 3, name: 'naranja', hex: '#f97316' },
  { age: 4, name: 'amarillo', hex: '#eab308' },
];

// Tailwind class map for known color names
const TAILWIND_MAP = {
  verde: { bg: 'bg-green-500', text: 'text-green-500', border: 'border-green-500' },
  azul: { bg: 'bg-blue-500', text: 'text-blue-500', border: 'border-blue-500' },
  rojo: { bg: 'bg-red-500', text: 'text-red-500', border: 'border-red-500' },
  naranja: { bg: 'bg-orange-500', text: 'text-orange-500', border: 'border-orange-500' },
  amarillo: { bg: 'bg-yellow-500', text: 'text-yellow-500', border: 'border-yellow-500' },
  morado: { bg: 'bg-purple-500', text: 'text-purple-500', border: 'border-purple-500' },
  rosa: { bg: 'bg-pink-500', text: 'text-pink-500', border: 'border-pink-500' },
  gris: { bg: 'bg-gray-500', text: 'text-gray-500', border: 'border-gray-500' },
  // Legacy English keys
  blue: { bg: 'bg-blue-500', text: 'text-blue-500', border: 'border-blue-500' },
  green: { bg: 'bg-green-500', text: 'text-green-500', border: 'border-green-500' },
  yellow: { bg: 'bg-yellow-500', text: 'text-yellow-500', border: 'border-yellow-500' },
  orange: { bg: 'bg-orange-500', text: 'text-orange-500', border: 'border-orange-500' },
  red: { bg: 'bg-red-500', text: 'text-red-500', border: 'border-red-500' },
};

// Legacy COLOR_MAP for backward compatibility with existing pages
export const COLOR_MAP = {
  blue: { bg: 'bg-blue-500', text: 'text-blue-500', border: 'border-blue-500', name: 'Azul', hex: '#3b82f6' },
  green: { bg: 'bg-green-500', text: 'text-green-500', border: 'border-green-500', name: 'Verde', hex: '#22c55e' },
  yellow: { bg: 'bg-yellow-500', text: 'text-yellow-500', border: 'border-yellow-500', name: 'Amarillo', hex: '#eab308' },
  orange: { bg: 'bg-orange-500', text: 'text-orange-500', border: 'border-orange-500', name: 'Naranja', hex: '#f97316' },
  red: { bg: 'bg-red-500', text: 'text-red-500', border: 'border-red-500', name: 'Rojo', hex: '#ef4444' },
};

export const PRICE_COLORS = ['blue', 'green', 'yellow', 'orange', 'red'];

/**
 * Get the color info for a bicycle based on its model_year and the current year.
 * Uses age-based calculation: age = currentYear - modelYear
 * @param {number} modelYear - The bicycle's model year
 * @param {Array} yearColors - The year-color config array from backend (optional)
 * @returns {{ name: string, hex: string, age: number }} color info
 */
export function getColorInfoForBike(modelYear, yearColors = null) {
  const colors = yearColors && yearColors.length > 0 ? yearColors : DEFAULT_YEAR_COLORS;
  const currentYear = new Date().getFullYear();
  const age = modelYear ? Math.max(0, currentYear - modelYear) : 0;
  const cappedAge = Math.min(age, colors.length - 1);
  
  const match = colors.find(c => c.age === cappedAge);
  return match
    ? { ...match, age }
    : { ...colors[0], age };
}

/**
 * Legacy function - get color key for a model year.
 * Now returns the configured color name (e.g. 'verde', 'azul').
 */
export function getColorForModelYear(modelYear, yearColors = null) {
  const info = getColorInfoForBike(modelYear, yearColors);
  return info.name;
}

// Get year age from a color based on current year
export function getYearAgeFromColor(color, currentYear) {
  if (!PRICE_COLORS.includes(color)) return 0;
  const colorIndex = PRICE_COLORS.indexOf(color);
  const currentColorIndex = currentYear % 5;
  const diff = (currentColorIndex - colorIndex + 5) % 5;
  return diff;
}

// Price color indicator dot - supports both tailwind class names and raw hex
export function PriceColorDot({ color, hex, size = 'md', showTooltip = false, yearAge }) {
  const tw = TAILWIND_MAP[color];
  
  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
    xl: 'w-6 h-6'
  };
  
  if (tw) {
    return (
      <div 
        className={`${sizeClasses[size]} rounded-full ${tw.bg} flex-shrink-0`}
        title={showTooltip ? `${color} - Año ${yearAge ?? '?'}` : undefined}
      />
    );
  }
  
  // Fallback: use hex directly
  return (
    <div 
      className={`${sizeClasses[size]} rounded-full flex-shrink-0`}
      style={{ backgroundColor: hex || '#6b7280' }}
      title={showTooltip ? `${color} - Año ${yearAge ?? '?'}` : undefined}
    />
  );
}

// Color legend component - updated to work with new config format
export function ColorLegend({ legend, compact = false, hidePercentages = false }) {
  if (!legend || legend.length === 0) return null;
  
  const sortedLegend = [...legend].sort((a, b) => a.year_age - b.year_age);
  
  if (compact) {
    return (
      <div className="flex flex-wrap gap-2">
        {sortedLegend.map(item => {
          const hex = item.color_hex || (COLOR_MAP[item.color]?.hex) || '#6b7280';
          return (
            <div key={item.color} className="flex items-center gap-1.5 text-xs">
              <div 
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: hex }}
              />
              <span className="text-slate-600">
                {hidePercentages ? `Año ${item.year_age}` : `Año ${item.year_age} (${item.percentage})`}
              </span>
            </div>
          );
        })}
      </div>
    );
  }
  
  return (
    <div className="bg-slate-50 rounded-lg p-4">
      <h4 className="font-medium text-sm text-slate-700 mb-3">Leyenda de Colores</h4>
      <div className="grid grid-cols-5 gap-3">
        {sortedLegend.map(item => {
          const hex = item.color_hex || (COLOR_MAP[item.color]?.hex) || '#6b7280';
          return (
            <div key={item.color} className="text-center">
              <div 
                className="w-8 h-8 rounded-full mx-auto mb-1"
                style={{ backgroundColor: hex }}
              />
              <p className="text-xs font-medium text-slate-700">{item.label}</p>
              {!hidePercentages && <p className="text-xs text-slate-500">{item.percentage}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Calculate second-hand price using YEARS formula
export function calculateYearBasedPrice(bike, avgPriceByModel, colorLegend, yearColors = null) {
  const model = bike.model;
  const modelPrices = avgPriceByModel?.[model];
  const currentYear = new Date().getFullYear();
  
  let modelYear = bike.model_year;
  if (!modelYear) {
    return { 
      netPrice: 0, ivaPrice: 0, yearAge: 0, multiplier: 1, 
      avgPrice: 0, modelYear: null, calculatedColor: 'verde',
      calculatedHex: '#22c55e', percentage: '+15%'
    };
  }
  
  const yearAge = Math.max(0, currentYear - modelYear);
  const colorInfo = getColorInfoForBike(modelYear, yearColors);
  
  const YEAR_MULTIPLIERS = {
    0: 1.15, 1: 0.85, 2: 0.70, 3: 0.55, 4: 0.45,
  };
  const multiplier = YEAR_MULTIPLIERS[Math.min(yearAge, 4)] ?? 0.45;
  
  if (!modelPrices) {
    const percentage = yearAge === 0 ? '+15%' : yearAge === 1 ? '-15%' : yearAge === 2 ? '-30%' : yearAge === 3 ? '-45%' : '-55%';
    return { 
      netPrice: 0, ivaPrice: 0, yearAge, multiplier: 1, avgPrice: 0, 
      modelYear, calculatedColor: colorInfo.name, calculatedHex: colorInfo.hex, percentage
    };
  }
  
  const avgPrice = modelPrices.avg_price;
  const priceWithIva = avgPrice * 1.21;
  const finalPrice = priceWithIva * multiplier;
  const roundedPrice = roundToAttractivePrice(finalPrice);
  const percentage = yearAge === 0 ? '+15%' : yearAge === 1 ? '-15%' : yearAge === 2 ? '-30%' : yearAge === 3 ? '-45%' : '-55%';
  
  return { 
    netPrice: avgPrice, ivaPrice: roundedPrice, yearAge, multiplier, avgPrice,
    percentage, modelYear, calculatedColor: colorInfo.name, calculatedHex: colorInfo.hex
  };
}

// Calculate second-hand price using MONTHS formula (original)
export function calculateMonthBasedPrice(bike) {
  const purchasePrice = bike.purchase_price || 0;
  const maintenanceCost = bike.total_maintenance_cost || 0;
  const basePrice = purchasePrice + maintenanceCost;
  
  if (basePrice === 0) return { netPrice: 0, ivaPrice: 0, monthsDiff: 0, multiplier: 1 };
  
  const purchaseDate = bike.purchase_date ? new Date(bike.purchase_date) : new Date(bike.created_at);
  const now = new Date();
  const monthsDiff = (now.getFullYear() - purchaseDate.getFullYear()) * 12 + 
                     (now.getMonth() - purchaseDate.getMonth());
  
  let multiplier;
  if (monthsDiff <= 2) {
    multiplier = 1.10;
  } else {
    const additionalMonths = monthsDiff - 2;
    multiplier = 1.10 - (additionalMonths * 0.02);
    multiplier = Math.max(multiplier, 0.20);
  }
  
  const netPrice = basePrice * multiplier;
  const ivaPrice = netPrice * 1.21;
  
  return { netPrice, ivaPrice, monthsDiff, multiplier, basePrice };
}

// Round price down to an attractive value based on price range
export function roundToAttractivePrice(price) {
  if (price <= 0) return 0;
  
  if (price < 1000) {
    const attractivePrices = [990, 950, 890, 850, 790, 750, 690, 650, 590, 550, 490, 450, 390, 350, 290, 250, 190, 150, 90];
    for (const target of attractivePrices) {
      if (price >= target) return target;
    }
    return Math.max(Math.floor(price / 10) * 10 - 10, 0);
  }
  
  if (price < 3000) {
    const base = Math.floor(price / 100) * 100;
    if (base + 90 <= price) return base + 90;
    return base - 10;
  }
  
  const base = Math.floor(price / 1000) * 1000;
  if (price >= base + 990) return base + 990;
  if (price >= base + 490) return base + 490;
  return base > 0 ? base - 10 : 990;
}

// Format currency
export function formatCurrency(value) {
  return new Intl.NumberFormat('es-ES', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(value);
}
